import { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Stack,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CustomMarkdownEditor from "./CustomMarkdownEditor";
import Divider from "@mui/material/Divider";

import "react-quill/dist/quill.snow.css"; // Quill styles
import "quill-emoji/dist/quill-emoji.css"; // Emoji styles
import PlaceholderInput from "./PlaceholderInput";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import { Add, Search } from "@mui/icons-material";
import SectionDisplay from "./Components/SectionDisplay";
import { useAuth } from "../../hooks/AuthProvider";
import {
  TEMPLATE_APP_CATALOGS,
  TEMPLATE_CATALOG_PRODUCTS,
} from "../../config/urls";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SECTION_LIMIT = 10;
const PRODUCT_LIMIT = 30;

const SortableSection = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </Box>
  );
};

const MultipleProductsTemplateEditor = ({
  headerType,
  setHeaderType,
  headerText,
  setHeaderText,
  body,
  setBody,
  sections,
  setSections,
  thumbnail,
  setThumbnail,
  createSection,
  createProduct,
  sampleBodyFields,
  setsampleBodyFields,
  sampleHeaderFields,
  setsampleHeaderFields,
  footer,
  setFooter,
  headerDuplicatePlaceholderError,
  setHeaderDuplicatePlaceholderError,
  bodyDuplicatePlaceholderError,
  setBodyDuplicatePlaceholderError,
  headerTextError,
  setHeaderTextError,
}) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [catalogOptions, setCatalogOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  // Unfiltered first page, shared by the section pickers until they search
  const [defaultProductOptions, setDefaultProductOptions] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const latestProductsRequest = useRef(0);

  useEffect(() => {
    fetchAppCatalogs();
  }, []);

  useEffect(() => {
    if (selectedCatalog?.id) setProductsLoading(true);

    const timer = setTimeout(() => {
      fetchCatalogProducts();
    }, 700);

    return () => clearTimeout(timer);
  }, [selectedCatalog, productSearch]);

  // Set Header type as text as MPM templates require a text header
  useEffect(() => {
    setHeaderType("text");
  }, [])

  const fetchAppCatalogs = async () => {
    const token = await getValidToken();

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const res = await fetch(TEMPLATE_APP_CATALOGS, { headers });

      if (!res.ok) throw new Error("Failed to fetch catalogs");

      const data = await res.json();
      setCatalogOptions(data);

      if (data.length > 0) {
        setSelectedCatalog(data[0]);
      }
    } catch (error) {
      console.error("Error fetching catalogs:", error);
    } 
  };

  // Server-side catalog search, also used by the product rows in each section
  const searchCatalogProducts = async (search) => {
    const token = await getValidToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    const query = search?.trim()
      ? `?search=${encodeURIComponent(search.trim())}`
      : "";

    const res = await fetch(
      `${TEMPLATE_CATALOG_PRODUCTS}${selectedCatalog?.id}${query}`,
      { headers },
    );

    if (!res.ok) throw new Error("Failed to fetch products");

    return res.json();
  };

  const fetchCatalogProducts = async () => {
    if (!selectedCatalog?.id) return;

    // Searches can overlap; only the latest request may update the list
    const requestId = ++latestProductsRequest.current;
    setProductsLoading(true);

    try {
      const data = await searchCatalogProducts(productSearch);
      if (requestId === latestProductsRequest.current) {
        setProductOptions(data);
        if (!productSearch.trim()) setDefaultProductOptions(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      if (requestId === latestProductsRequest.current) {
        setProductsLoading(false);
      }
    }
  };

  const detectPlaceholders = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    const seen = new Set();
    let hasDuplicate = false;

    matches.forEach((match) => {
      if (seen.has(match)) {
        hasDuplicate = true;
      } else {
        seen.add(match);
      }
    });

    // Only keep the first occurrence of each placeholder
    const uniquePlaceholders = matches.filter(
      (match, idx) => matches.indexOf(match) === idx,
    );
    return { hasDuplicate, uniquePlaceholders };
  };

  const validateHeaderText = (text) => {
    // Emoji ranges (emoticons, symbols, pictographs, etc.)
    const emojiRegex =
      /\p{Emoji_Presentation}|\p{Extended_Pictographic}️|[\u{1F1E6}-\u{1F1FF}]/u;

    // Disallowed formatting characters
    const formattingCharsRegex = /[*~]/;

    // Newline check
    const newlineRegex = /[\r\n]/;

    if (emojiRegex.test(text)) {
      return "Emojis are not permitted in the header.";
    }
    if (newlineRegex.test(text)) {
      return "Header cannot contain line breaks.";
    }
    if (formattingCharsRegex.test(text)) {
      return "Header cannot contain formatting characters (*, ~).";
    }
    return "";
  };

  const handleFooterChange = (event) => {
    const newFooter = event.target.value;

    if (newFooter === "" || newFooter.trim().length > 0) {
      if (newFooter.length <= 60) {
        setFooter(newFooter);
      }
    }
  };

  const handleAddSection = () => {
    const newId = sections.length
      ? Math.max(...sections.map((s) => s.id)) + 1
      : 1;

    setSections((prev) => [...prev, createSection(newId)]);
  };

  const handleRemoveSection = (sectionId) => {
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const handleSectionTitleChange = (sectionId, newTitle) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return { ...section, title: newTitle };
      }),
    );
  };

  const handleAddProduct = (sectionId) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        const newProductId = section.products.length
          ? Math.max(...section.products.map((p) => p.id)) + 1
          : 1;

        return {
          ...section,
          products: [...section.products, createProduct(newProductId)],
        };
      }),
    );
  };

  const handleRemoveProduct = (sectionId, productId) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        if (section.products.length <= 1) return section;

        return {
          ...section,
          products: section.products.filter(
            (product) => product.id !== productId,
          ),
        };
      }),
    );
  };

  const handleChangeProduct = (sectionId, productId, newProduct) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;

        return {
          ...section,
          products: section.products.map((product) => {
            if (product.id !== productId) return product;
            return {
              ...product,
              title: newProduct?.name ?? "",
              retailerId: newProduct?.retailer_id ?? "",
            };
          }),
        };
      }),
    );
  };

  const handleReorderSections = (oldIndex, newIndex) => {
    setSections((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleReorderProducts = (sectionId, oldIndex, newIndex) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          products: arrayMove(section.products, oldIndex, newIndex),
        };
      }),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sectionIds = (sections || []).map((s) => `section-${s.id}`);

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sectionIds.indexOf(active.id);
      const newIndex = sectionIds.indexOf(over.id);
      handleReorderSections(oldIndex, newIndex);
    }
  };

  const totalProductsCount = (sections || []).reduce(
    (count, section) => count + (section.products?.length || 0),
    0,
  );
  const productsLimitExceeded = totalProductsCount > PRODUCT_LIMIT;
  const sectionsLimitExceeded = (sections?.length || 0) > SECTION_LIMIT;

  const selectedRetailerIds = (sections || [])
    .flatMap((s) => s.products || [])
    .map((p) => p.retailerId)
    .filter(Boolean);

  return (
    <Stack spacing={4}>
      {/* Header */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              Message Header
              {headerType === "none" && (
                <span style={{ opacity: 0.6 }}> (Optional)</span>
              )}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                aria-label="header-type"
                name="header-type"
                value={headerType}
                onChange={(event) => {
                  const selectedHeaderType = event.target.value;

                  switch (selectedHeaderType) {
                    case "text":
                      break;
                    default:
                      break;
                  }

                  setHeaderType(selectedHeaderType);
                }}
                row
                sx={{
                  width: { xs: "100%", md: "90%" },
                  gap: { xs: 1 },
                  flexWrap: "wrap",
                }}
              >
                <FormControlLabel
                  value="text"
                  control={
                    <Radio
                      color="primary"
                      sx={{
                        "&.Mui-checked": {
                          color: ColorsConstants.primaryBlue100,
                        },
                      }}
                    />
                  }
                  label="Text"
                />
              </RadioGroup>
            </FormControl>

            {headerType === "text" && (
              <Grid item xs={12}>
                <TextField
                  sx={{ width: "100%" }}
                  label="Header Text"
                  value={headerText}
                  onChange={(event) => {
                    const newHeader = event.target.value;
                    const errorMsg = validateHeaderText(newHeader);

                    setHeaderTextError(errorMsg);
                    setHeaderText(newHeader);

                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newHeader);
                    setHeaderDuplicatePlaceholderError(hasDuplicate);

                    // Only update sample fields if no error
                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleHeaderFields[idx] !== undefined
                          ? sampleHeaderFields[idx]
                          : "",
                      );

                      setsampleHeaderFields((prev) => {
                        // Only allow a single header parameter
                        if (newSamples.length > 1) {
                          return prev;
                        } else {
                          return newSamples;
                        }
                      });
                    }
                    // On a transient duplicate, keep existing sample fields as-is
                    // so previously entered values aren't lost once it resolves.
                  }}
                  error={
                    Boolean(headerTextError) || headerDuplicatePlaceholderError
                  }
                  helperText={
                    headerTextError
                      ? headerTextError
                      : headerDuplicatePlaceholderError
                        ? "Duplicate variable placeholders are not allowed."
                        : ""
                  }
                  inputProps={{
                    maxLength: 60,
                    style: { paddingRight: "40px" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "gray",
                          position: "absolute",
                          right: "10px",
                          top: "12px",
                        }}
                      >
                        {`${headerText.length}/60`}
                      </Typography>
                    ),
                  }}
                  required
                />
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  Maximum 1 parameter allowed · Emojis are not permitted.
                </Typography>
              </Grid>
            )}

            {!headerDuplicatePlaceholderError &&
              sampleHeaderFields.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                    Header Variable
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      gap: 4,
                    }}
                  >
                    {sampleHeaderFields.map((sample, index) => (
                      <Box
                        key={index}
                        sx={{
                          marginBottom: "16px",
                          display: "flex",
                          flex: { xs: 1, md: 0.5 },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ display: "inline-block", mr: 1 }}
                        >
                          {"{{" + (index + 1) + "}}:"}
                        </Typography>
                        <PlaceholderInput
                          sample={sample}
                          index={index}
                          sampleBodyFields={sampleHeaderFields}
                          setsampleBodyFields={setsampleHeaderFields}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Body */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Grid item xs={12}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  Message body *
                </Typography>

                {/* Replace the textarea with our custom editor */}
                <CustomMarkdownEditor
                  id="template-body"
                  value={body}
                  onChange={(newValue) => {
                    setBody(newValue);
                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newValue);
                    setBodyDuplicatePlaceholderError(hasDuplicate);

                    // Only update sample fields if no error
                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleBodyFields[idx] !== undefined
                          ? sampleBodyFields[idx]
                          : "",
                      );
                      setsampleBodyFields(newSamples);
                    }
                    // On a transient duplicate, keep existing sample fields as-is
                    // so previously entered values aren't lost once it resolves.
                  }}
                  maxLength={1024}
                />

                {bodyDuplicatePlaceholderError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Duplicate variable placeholders are not allowed.
                  </Typography>
                )}
              </div>
            </Grid>
            {!bodyDuplicatePlaceholderError && sampleBodyFields.length > 0 && (
              <Grid item xs={11}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Body Variables
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    width: "100%",
                    gap: 4,
                  }}
                >
                  {sampleBodyFields.map((sample, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginBottom: "16px",
                        display: "flex",
                        flex: { xs: 1, md: 0.5 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ display: "inline-block", mr: 1 }}
                      >
                        {"{{" + (index + 1) + "}}:"}
                      </Typography>
                      <PlaceholderInput
                        sample={sample}
                        sampleBodyFields={sampleBodyFields}
                        index={index}
                        setsampleBodyFields={setsampleBodyFields}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            <Divider />

            {/* Footer */}
            <Grid item xs={12}>
              <Typography variant="h6">
                Footer <span style={{ opacity: 0.6 }}>(Optional)</span>
              </Typography>
              <TextField
                sx={{
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
                label="You can use this space to add a tagline, a way to unsubscribe, etc."
                value={footer}
                onChange={handleFooterChange}
                inputProps={{
                  maxLength: 60,
                  style: { paddingRight: "40px" },
                }}
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "gray",
                        position: "absolute",
                        right: "10px",
                        top: "12px",
                      }}
                    >
                      {`${footer.length}/60`}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Products */}
      <Card>
        <CardContent>
          <Stack gap={3} sx={{ width: "100%" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Products{" "}
                  <Typography component="span" sx={{ color: ColorsConstants.error }}>
                    *
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ color: grayFontColor, mt: 0.5 }}>
                  Group products from your catalog into sections. Customers
                  browse each section directly inside the WhatsApp message.
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: grayFontColor, whiteSpace: "nowrap" }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: productsLimitExceeded
                      ? ColorsConstants.error
                      : "inherit",
                    fontWeight: productsLimitExceeded ? 700 : 400,
                  }}
                >
                  {totalProductsCount}
                </Typography>{" "}
                of {PRODUCT_LIMIT} products ·{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: sectionsLimitExceeded
                      ? ColorsConstants.error
                      : "inherit",
                    fontWeight: sectionsLimitExceeded ? 700 : 400,
                  }}
                >
                  {sections?.length || 0}
                </Typography>{" "}
                of {SECTION_LIMIT} sections
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={catalogOptions || []}
                  value={selectedCatalog}
                  onChange={(e, value) => {
                    setSelectedCatalog(value);
                    setProductSearch("");
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={(props, option) => (
                    <Stack
                      {...props}
                      key={option?.id}
                      direction="column"
                      gap={0.5}
                      sx={{
                        alignItems: "flex-start !important",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <Typography>{option?.name}</Typography>
                      <Typography
                        variant="body2"
                        color={ColorsConstants.primaryGray40}
                      >
                        Catalog ID: {option?.id}
                      </Typography>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Catalog" fullWidth required />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 14,
                    },
                  }}
                  noOptionsText="No catalogs found"
                />
                <Typography variant="caption" sx={{ color: grayFontColor }}>
                  Auto-detected from your connected WhatsApp Business Account.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  disabled={!selectedCatalog}
                  options={productOptions || []}
                  value={thumbnail}
                  onChange={(e, value) => {
                    setThumbnail(value);
                    setProductSearch("");
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === "input") {
                      setProductSearch(newValue);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.retailer_id === value?.retailer_id
                  }
                  filterOptions={(options) => options}
                  loading={productsLoading}
                  loadingText="Searching products..."
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={(props, option) => (
                    <Stack
                      {...props}
                      key={option?.retailer_id}
                      direction="column"
                      gap={0.5}
                      sx={{
                        alignItems: "flex-start !important",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <Typography>{option?.name}</Typography>
                      <Typography
                        variant="body2"
                        color={ColorsConstants.primaryGray40}
                      >
                        Retailer ID: {option?.retailer_id}
                      </Typography>
                    </Stack>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Thumbnail Product (optional)"
                      placeholder="Search by product name"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <Search
                            sx={{ color: ColorsConstants.primaryGray40, mr: 1 }}
                          />
                        ),
                        endAdornment: (
                          <>
                            {productsLoading && (
                              <CircularProgress color="inherit" size={18} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 14,
                    },
                  }}
                  noOptionsText="No products found"
                />
                <Typography variant="caption" sx={{ color: grayFontColor }}>
                  Shown as the message's preview image in the chat list.
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                border: `1px dashed ${ColorsConstants.primaryBlue80}`,
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={sectionIds}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack gap={2} divider={<Divider />} sx={{ width: "100%" }}>
                    {sections?.length > 0 &&
                      sections.map((section) => (
                        <SortableSection
                          key={section?.id}
                          id={`section-${section.id}`}
                        >
                          {(handleProps) => (
                            <SectionDisplay
                              section={section}
                              dragHandleProps={handleProps}
                              onRemoveSection={handleRemoveSection}
                              onSectionTitleChange={handleSectionTitleChange}
                              onAddProduct={handleAddProduct}
                              onRemoveProduct={handleRemoveProduct}
                              onChangeProduct={handleChangeProduct}
                              onReorderProducts={handleReorderProducts}
                              catalogId={selectedCatalog?.id}
                              defaultProductOptions={defaultProductOptions}
                              searchProducts={searchCatalogProducts}
                              selectedRetailerIds={selectedRetailerIds}
                              disableAddProduct={
                                totalProductsCount >= PRODUCT_LIMIT
                              }
                            />
                          )}
                        </SortableSection>
                      ))}
                  </Stack>
                </SortableContext>
              </DndContext>

              {sections?.length < SECTION_LIMIT && (
                <ButtonOne
                  variant="outlined"
                  title="Add another section"
                  startIcon={<Add />}
                  onClick={handleAddSection}
                  sx={{
                    width: "100%",
                    mt: sections?.length > 0 ? 2 : 0,
                    borderStyle: "dashed",
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: grayFontColor }}>
              Up to {PRODUCT_LIMIT} products across up to {SECTION_LIMIT}{" "}
              sections per message.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};
export default MultipleProductsTemplateEditor;
